import is from '@sindresorhus/is';
import type { RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

export type TextSelectionResult = {
    readonly rect: DOMRect;
    readonly getRange: () => Range | null;
};

const findDescriptionSectionAncestor = (node: Node | null): HTMLElement | null => {
    let el: HTMLElement | null = node instanceof HTMLElement ? node : (node?.parentElement ?? null);
    while (!is.null(el)) {
        if (el.getAttribute('data-anchor-kind') === 'description-section') return el;
        el = el.parentElement;
    }
    return null;
};

const findContentRoot = (sectionEl: HTMLElement): HTMLElement | null =>
    sectionEl.querySelector<HTMLElement>('[data-section-content-root="true"]');

const captureSelectionResult = (container: HTMLElement): { rect: DOMRect; range: Range } | null => {
    const sel = window.getSelection();
    if (is.null(sel) || sel.isCollapsed || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (range.toString().trim().length === 0) return null;
    const sectionEl = findDescriptionSectionAncestor(range.startContainer);
    if (is.null(sectionEl) || !container.contains(sectionEl)) return null;
    if (findDescriptionSectionAncestor(range.endContainer) !== sectionEl) return null;
    const contentRoot = findContentRoot(sectionEl);
    if (is.null(contentRoot)) return null;
    if (!contentRoot.contains(range.startContainer) || !contentRoot.contains(range.endContainer)) return null;
    return { rect: range.getBoundingClientRect(), range: range.cloneRange() };
};

export const useTextSelection = (
    containerRef: RefObject<HTMLElement | null>,
): { readonly selection: TextSelectionResult | null; readonly clearSelection: () => void } => {
    const [selection, setSelection] = useState<TextSelectionResult | null>(null);
    const rangeRef = useRef<Range | null>(null);
    const suppressRef = useRef(false);

    const clearSelection = useCallback(() => {
        rangeRef.current = null;
        setSelection(null);
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (is.null(container)) return;

        const handleMouseUp = (): void => {
            if (suppressRef.current) {
                suppressRef.current = false;
                return;
            }
            const captured = captureSelectionResult(container);
            if (is.null(captured)) {
                rangeRef.current = null;
                setSelection(null);
                return;
            }
            rangeRef.current = captured.range;
            setSelection({
                rect: captured.rect,
                getRange: () => rangeRef.current,
            });
        };

        const handleMouseDown = (event: MouseEvent): void => {
            if (event.button === 2) {
                return;
            }
            const target = event.target instanceof HTMLElement ? event.target : null;
            if (!is.null(target) && !is.null(target.closest('[data-comment-button]'))) {
                suppressRef.current = true;
                return;
            }
            rangeRef.current = null;
            setSelection(null);
        };

        const handleScroll = (): void => {
            if (is.null(rangeRef.current)) return;
            rangeRef.current = null;
            setSelection(null);
        };

        document.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('scroll', handleScroll, { capture: true });
        };
    }, [containerRef]);

    return { selection, clearSelection };
};
